import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, Calendar, Clock, Users, ArrowRight } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingConfirmationPage = () => {
  const { bookingId } = useParams();
  const { t, getLocalizedField } = useLanguage();
  const [booking, setBooking] = useState(null);
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const bookingRes = await axios.get(`${API}/bookings/${bookingId}`);
        setBooking(bookingRes.data);
        
        const tripRes = await axios.get(`${API}/trips/${bookingRes.data.trip_id}`);
        setTrip(tripRes.data);
      } catch (error) {
        console.error('Error fetching booking:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <p className="text-[#4B5563]">Booking not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[#F8FAFC]" data-testid="booking-confirmation-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <div className="text-center mb-12 animate-fade-up">
          <div className="w-20 h-20 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} weight="fill" className="text-[#10B981]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#000000] font-['Playfair_Display'] mb-4">
            {t('booking.success')}
          </h1>
          <p className="text-[#4B5563]">
            {t('booking.confirmationSent')}
          </p>
        </div>

        {/* Booking Details */}
        <Card className="card-shadow animate-fade-up stagger-1">
          <CardContent className="p-8">
            {/* Trip Info */}
            {trip && (
              <div className="flex gap-4 mb-6 pb-6 border-b border-[#E2E8F0]">
                <img
                  src={trip.image_url}
                  alt={getLocalizedField(trip, 'name')}
                  className="w-24 h-24 rounded-xl object-cover"
                />
                <div>
                  <h2 className="text-xl font-bold text-[#000000] font-['Playfair_Display']">
                    {getLocalizedField(trip, 'name')}
                  </h2>
                  <p className="text-[#4B5563] text-sm mt-1">
                    {trip.duration_hours}h • Max {trip.max_participants} participants
                  </p>
                </div>
              </div>
            )}

            {/* Booking Info */}
            <div className="space-y-4 mb-6 pb-6 border-b border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <Calendar size={20} className="text-[#b7ede0]" />
                <div>
                  <p className="text-sm text-[#4B5563]">Date</p>
                  <p className="font-medium text-[#000000]">{booking.booking_date}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={20} className="text-[#b7ede0]" />
                <div>
                  <p className="text-sm text-[#4B5563]">Horaire</p>
                  <p className="font-medium text-[#000000]">{booking.time_slot}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users size={20} className="text-[#b7ede0]" />
                <div>
                  <p className="text-sm text-[#4B5563]">Participants</p>
                  <p className="font-medium text-[#000000]">
                    {booking.is_privatization ? 'Privatisation' : `${booking.participants.length} personne(s)`}
                  </p>
                </div>
              </div>
            </div>

            {/* Participants List */}
            <div className="mb-6 pb-6 border-b border-[#E2E8F0]">
              <h3 className="font-medium text-[#000000] mb-3">Liste des participants</h3>
              <div className="space-y-2">
                {booking.participants.map((p, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-[#4B5563]">
                      {p.first_name} {p.last_name}
                    </span>
                    <span className="text-[#4B5563]">
                      {p.age} ans • {p.phone}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-[#000000]">{t('booking.total')}</span>
              <span className="text-3xl font-bold text-[#000000]">{booking.total_amount}€</span>
            </div>
          </CardContent>
        </Card>

        {/* Booking Reference */}
        <div className="mt-6 p-4 bg-[#b7ede0]/10 rounded-xl text-center animate-fade-up stagger-2">
          <p className="text-sm text-[#4B5563] mb-1">Référence de réservation</p>
          <p className="font-mono font-bold text-[#000000]">{booking.id.toUpperCase().slice(0, 8)}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8 animate-fade-up stagger-3">
          <Link to="/" className="flex-1">
            <Button variant="outline" className="w-full rounded-full">
              {t('nav.home')}
            </Button>
          </Link>
          <Link to="/trips" className="flex-1">
            <Button className="w-full bg-[#b7ede0] hover:bg-[#9ee5d4] text-[#000000] rounded-full gap-2">
              {t('trips.title')}
              <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
